import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { cacheKeys, useCustomMutation } from '../../graphql';
import { useEnsureUnlocked } from '../../offline/useEnsureUnlocked';

type UpdateAssetProps = {
  assetId: string;
};

export const useUpdateAsset = (props: UpdateAssetProps) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useCustomMutation({
    query: UPDATE_ASSET,
  });
  const ensureUnlocked = useEnsureUnlocked();
  // Same signature as the mutation's own `mutateAsync`; refuses first while a
  // queued submission still touches this asset.
  const updateAsset = useCallback(
    async (...args: Parameters<typeof mutateAsync>) => {
      await ensureUnlocked(props.assetId);
      return mutateAsync(...args);
    },
    [ensureUnlocked, mutateAsync, props.assetId]
  ) as typeof mutateAsync;
  const filter = {
    asset: { eq: props.assetId },
  };

  const cacheKey = cacheKeys('workspaceAsset').list({ filter });

  const clearCache = async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKey });
  };

  return {
    updateAsset,
    clearCache,
    isUpdating: isLoading,
  };
};

const UPDATE_ASSET = `
  mutation Asset($input: UpdateAssetInput!) {
    asset {
      updateAsset(input: $input) {
        id
      }
    }
  }
`;
